import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import {
  $transferRequestsGetDocumentsStore,
  $transferRequestsUploadFileStore,
  $transferRequestsDeleteFileStore,
  $transferUpdateStatusStore,
  $generateOTPCodeStore,
  $downloadDocumentStore,
} from '@lib/store';
import { AmountUtils, DateUtils, PaletteUtils, FormatUtils, cn } from '@shared/utils';
import { GenerateCodeTypes, TransferRequestStatusEnum, type TransferRequestResponse } from '@shared/api';
import { ExchangeRate } from '../exchangeRate';
import { Button, FileUpload, Icon, Modal, Spinner, Text } from '@shared/widgets';
import WarningIcon from '@assets/icons/warning.svg';
import DownloadIcon from '@assets/icons/download.svg';

interface TransferRequestConfirmModalProps {
  isOpen: boolean;
  onClose: (withRedirect?: boolean) => void;
  withRedirect?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  readOnly?: boolean;
  noDocumentUpload?: boolean;
  transactionId?: string;
  transferData: TransferRequestResponse;
  isLimitReached?: boolean;
  onOpenOtpModal?: (data: TransferRequestResponse) => void;
  onCloseOtpModal?: () => void;
  onSubmit?: () => void | Promise<void>;
  onUpdate?: () => void;
  onReject?: () => void;
}

export const TransferRequestConfirmModal = (props: TransferRequestConfirmModalProps) => {
  const uploadedDocumentsStore = useUnit($transferRequestsGetDocumentsStore.store);
  const updateStatusStore = useUnit($transferUpdateStatusStore.store);
  const docsUploadStore = useUnit($transferRequestsUploadFileStore.store);
  const docsDeleteStore = useUnit($transferRequestsDeleteFileStore.store);

  const [isTransactionProcessing, setIsTransactionProcessing] = useState<boolean>(false);

  const { t } = useTranslation('payments');

  useEffect(() => {
    if (!props.transactionId) return;
    $transferRequestsGetDocumentsStore.events.getDocuments({ TransferRequestId: props.transactionId });
    return () => $transferRequestsGetDocumentsStore.events.clear();
  }, [props.transactionId]);

  useEffect(() => {
    if (!props.transactionId) return;
    $transferRequestsGetDocumentsStore.events.getDocuments({ TransferRequestId: props.transactionId });
  }, [docsDeleteStore, docsUploadStore]);

  const rejectHandler = async () => {
    await $transferUpdateStatusStore.events.updateStatus({ id: props.transferData.id, status: TransferRequestStatusEnum.Cancelled });
    props.onUpdate?.();
    props.onCloseOtpModal?.();
    props.onReject?.();
    props.onClose();
  };
  const submitHandler = async () => {
    try {
      setIsTransactionProcessing(true);
      await props.onSubmit?.();
      if (props.transferData.status === TransferRequestStatusEnum.WaitingForApproval)
        await $generateOTPCodeStore.events.generateCode({ OtpType: GenerateCodeTypes.UpdateTransferRequest, seed: props.transferData.id });
      props.onOpenOtpModal?.(props.transferData);
    } catch {
      props.onClose();
    } finally {
      setIsTransactionProcessing(false);
    }
  };

  const uploadFileHandler = async (file: File) => {
    if (!props.transactionId) return;
    await $transferRequestsUploadFileStore.events.createDocument({ transferRequestId: props.transactionId, file });
    $transferRequestsGetDocumentsStore.events.getDocuments({ TransferRequestId: props.transactionId });
  };
  const deleteFileHandler = async (id: string) => {
    if (!props.transactionId) return;
    await $transferRequestsDeleteFileStore.events.update({ TransferRequestId: props.transactionId!, DocumentId: id });
    $transferRequestsGetDocumentsStore.events.getDocuments({ TransferRequestId: props.transactionId });
  };
  const downloadHandler = (id: string) => $downloadDocumentStore.events.download({ TransferRequestId: props.transactionId!, DocumentId: id });

  const file = useMemo(() => {
    if (uploadedDocumentsStore.data.length)
      return {
        id: uploadedDocumentsStore.data[0].id,
        name: uploadedDocumentsStore.data[0].name,
        type: uploadedDocumentsStore.data[0].attachmentFile?.mimetype || '',
        file: uploadedDocumentsStore.data[0].attachmentFile?.base64 || '',
      };
    return null;
  }, [uploadedDocumentsStore.data]);
  const leftColPropsMap = useMemo(
    () => [
      {
        label: t('modals.transferRequestConfirm.fields.type'),
        value: FormatUtils.transactionTypeTransformation(t(`transferRequestType.${props.transferData.type}`, { ns: 'common' })),
      },
      {
        label: t('modals.transferRequestConfirm.fields.dateOfExecution'),
        value: DateUtils.formatDate(props.transferData.startDate || props.transferData.createdOn || ''),
      },
      {
        label: t('modals.transferRequestConfirm.fields.recipient'),
        value: props.transferData.beneficiaryAccount?.name || props.transferData.recipient?.name,
      },
      {
        label: t('modals.transferRequestConfirm.fields.senderCurrency'),
        value: props.transferData.senderCurrency,
      },
      // TODO: Temporarily hidden
      // {
      //   label: t('modals.transferRequestConfirm.fields.fastPayment'),
      //   value: props.transferData.fastPayment ? t('boolean.yes', { ns: 'common' }) : t('boolean.no', { ns: 'common' }),
      // },
      props.transferData.beneficiaryCurrency !== props.transferData.senderCurrency
        ? {
            label: t('modals.transferRequestConfirm.fields.exchangeRate'),
            value: `${AmountUtils.transformToAmountWithCurrency(1, props.transferData.senderCurrency)} = ${AmountUtils.transformToAmountWithCurrency(props.transferData.exchangeRate, props.transferData.beneficiaryCurrency as string)}`,
            additionalComponent: <ExchangeRate />,
          }
        : null,
    ],
    [props.transferData, t],
  );
  const rightColPropsMap = useMemo(
    () => [
      {
        label: t('modals.transferRequestConfirm.fields.beneficiaryCurrency'),
        value: props.transferData.beneficiaryCurrency,
      },
      // {
      //   label: t('modals.transferRequestConfirm.fields.senderAmount'),
      //   value: `${AmountUtils.transformToAmountWithCurrency(props.transferData.senderAmount, props.transferData.senderCurrency)}`,
      // },
      {
        label: t('modals.transferRequestConfirm.fields.beneficiaryAmount'),
        value: `${AmountUtils.transformToAmountWithCurrency(props.transferData.beneficiaryAmount, props.transferData.beneficiaryCurrency)}`,
      },
      // {
      //   label: t('modals.transferRequestConfirm.fields.fees'),
      //   value: `${AmountUtils.transformToAmountWithCurrency(props.transferData.fee, props.transferData.senderCurrency)}`,
      // },
      // {
      //   label: t('modals.transferRequestConfirm.fields.totalAmount'),
      //   value: `${AmountUtils.transformToAmountWithCurrency(
      //     typeof props.transferData.fee !== 'number' ? props.transferData.senderAmount : props.transferData.totalAmount,
      //     props.transferData.senderCurrency,
      //   )}`,
      // },
    ],
    [props.transferData, t],
  );
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={() => props.onClose(props.withRedirect)}
      title={
        <>
          <div className='flex items-center justify-between'>
            <Text className='font-[Prata]' size='3xl'>
              {AmountUtils.transformToAmountWithCurrency(
                typeof props.transferData.fee !== 'number' ? props.transferData.beneficiaryAmount : props.transferData.beneficiaryAmount,
                props.transferData.beneficiaryCurrency,
              )}
            </Text>
          </div>
          {/* <Text size='sm'>
            {t('modals.transferRequestConfirm.title')}{' '}
            {AmountUtils.transformToAmountWithCurrency(props.transferData.fee, props.transferData.senderCurrency)}
          </Text> */}
          {(props.transferData.startDate || props.transferData.createdOn) && (
            <Text size='sm'>{DateUtils.formatDate(props.transferData.startDate || props.transferData.createdOn || '', { withTime: true })}</Text>
          )}
        </>
      }
      width='500px'
    >
      <Spinner show={isTransactionProcessing} position />
      <>
        {!props.readOnly && (
          <div className='mt-5 flex gap-3'>
            <Button fullWidth color='outlined' disabled={updateStatusStore.loading} onClick={rejectHandler}>
              {props.cancelLabel || t('modals.transferRequestConfirm.buttons.reject')}
            </Button>
            <Button fullWidth color='primary' onClick={submitHandler}>
              {props.confirmLabel || t('modals.transferRequestConfirm.buttons.approve')}
            </Button>
          </div>
        )}
        <div
          className={cn(
            'mb-3 mt-4 flex p-3',
            PaletteUtils.isInsigniaPalette && 'bg-ice dark:bg-soft-black',
            PaletteUtils.isVcfcPalette && 'bg-white dark:bg-light/5',
          )}
        >
          <div
            className={cn(
              'mr-3 flex h-9 w-9 flex-none items-center justify-center rounded-3xl text-ice',
              PaletteUtils.isInsigniaPalette && 'bg-soft-black dark:bg-primary',
              PaletteUtils.isVcfcPalette && 'bg-primary',
            )}
          >
            {props.transferData.fromAccount?.name[0] || 'A'}
          </div>
          <div className='my-auto ml-3 flex flex-col justify-between'>
            <Text>{props.transferData.fromAccount?.name}</Text>
          </div>
        </div>
        <div className='flex w-full'>
          <div className='flex w-1/2 flex-col items-start justify-start'>
            {leftColPropsMap?.map((element, idx) => (
              <div key={`${element?.label}_${idx}`} className='mb-3'>
                <Text size='sm' color='secondary'>
                  {element?.label}
                </Text>
                <Text weight='semibold' size='sm' className='flex gap-2'>
                  {element?.value}
                  {element?.additionalComponent}
                </Text>
              </div>
            ))}
          </div>
          <div className='flex w-1/2 flex-col items-start justify-start'>
            {rightColPropsMap?.map((element, idx) => (
              <div key={`${element?.label}_${idx}`} className='mb-3'>
                <Text size='sm' color='secondary'>
                  {element?.label}
                </Text>
                <Text weight='semibold' size='sm'>
                  {element?.value}
                </Text>
              </div>
            ))}
          </div>
        </div>
        {!props.noDocumentUpload && (
          <>
            {props.isLimitReached && (
              <div
                className={cn(
                  'mt-4 w-full rounded-sm border-none p-2 pb-0.5',
                  PaletteUtils.isVcfcPalette && 'bg-ice p-2 dark:bg-light/5',
                  PaletteUtils.isInsigniaPalette && 'bg-ice dark:bg-slate',
                )}
              >
                <Text size='sm' color='secondary'>
                  {t('modals.transferRequestConfirm.documents.title')}
                </Text>
                <div className={cn('flex flex-col gap-2', PaletteUtils.isVcfcPalette && 'bg-white p-3 dark:bg-light/5')}>
                  <div className='flex items-start justify-start'>
                    <div className='mr-3 rounded-sm bg-light-carmine-pink p-3'>
                      <Icon icon={<WarningIcon />} color='red' />
                    </div>
                    <div className='flex flex-col items-start justify-start'>
                      <Text>{t('modals.transferRequestConfirm.documents.description')}</Text>
                      <ul className='mt-2 pl-4'>
                        <li className='list-disc'>{t('modals.transferRequestConfirm.documents.fields.agreements')}</li>
                        <li className='list-disc'>{t('modals.transferRequestConfirm.documents.fields.invoices')}</li>
                        <li className='list-disc'>{t('modals.transferRequestConfirm.documents.fields.receipts')}</li>
                      </ul>
                    </div>
                  </div>
                  {PaletteUtils.isVcfcPalette && <Text>{t('modals.transferRequestConfirm.documents.helpText')}</Text>}
                </div>
              </div>
            )}
            {(uploadedDocumentsStore.loading || docsUploadStore.loading || docsDeleteStore.loading) && <Spinner />}
            {!uploadedDocumentsStore.loading && !docsUploadStore.loading && !docsDeleteStore.loading && (
              <FileUpload
                value={file}
                disabled={props.transferData.status === TransferRequestStatusEnum.WaitingForApproval}
                onChange={(file) => uploadFileHandler(file as File)}
                onDelete={(file) => deleteFileHandler(file.id!)}
              />
            )}
          </>
        )}
        {props.noDocumentUpload && props.transactionId && uploadedDocumentsStore.data.length > 0 && (
          <div className='mt-2 flex flex-col items-start justify-start gap-2'>
            {uploadedDocumentsStore.data.map((file) => (
              <Button key={file.id} className='p-1' color='icon' onClick={() => downloadHandler(file.id)}>
                <Icon icon={<DownloadIcon />} color={PaletteUtils.isInsigniaPalette ? 'primary' : undefined} />
                {file.name}
              </Button>
            ))}
          </div>
        )}
      </>
    </Modal>
  );
};
